import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CreateVideoModal from "../AiModule/CreateVideoModal";
import { toastify } from "../../components/Toast";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UrlConverter } from "../../utils/UrlConverter";
import { CircularLoader } from "../Loader/CircularLoader";

const CreateBrand = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    internalName: "",
    description: "",
    industry: "",
    website: "",
    targetMarket: "",
    uniqueValue: "",
    mainJob: "",
    differentiation: "",
    howItHelps: "",
    features: "",
    benefits: "",
    tone: "",
    colors: [],
    brandDocument: null,
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [apiError, setApiError] = useState("");
  const [brandId, setBrandId] = useState("");
  const [selectedContentFilter, ] = useState("ads");
  const [isGenerating, setIsGenerating] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors.length > 0) setValidationErrors([]);
  };

  const handleColorChange = (index, color) => {
    const newColors = [...formData.colors];
    newColors[index] = color;
    setFormData((prev) => ({ ...prev, colors: newColors }));
  };

  const addColor = () => {
    const newColors = [...formData.colors, "#3B82F6"];
    setFormData((prev) => ({ ...prev, colors: newColors }));
  };

  const removeColor = (index) => {
    if (formData.colors.length) {
      const newColors = formData.colors.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, colors: newColors }));
    }
  };

  const handleAIAnalysis = async () => {
    if (!formData.website) {
      alert("Please enter a website URL first");
      return;
    }
    setIsAnalyzing(true);
    const token = localStorage.getItem("token");
    // Map formData to API payload
    const payload = {
      websiteUrl: formData.website,
      isDirectURL: true,
    };
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}brands`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Include the token in the request header
          },
        }
      );
      toastify("success", response.data.message);
      setBrandId(response.data.data._id);
      setTimeout(() => {
        setFormData((prev) => ({
          ...prev,
          name: response.data.data.brandName,
          internalName: response.data.data.internalName,
          description:
            response.data.data.description ||
            "AI-powered productivity software for modern teams",
          industry: response.data.data.industry || "Technologys",
          targetMarket:
            response.data.data.targetMarket ||
            "Small to medium businesses, remote teams, productivity-focused professionals seeking streamlined workflow solutions",
          mainJob:
            response.data.data.mainJobOfProduct ||
            "Facilitates remote project management and team coordination.\nEnables real-time communication with team members across different time zones.\nIntegrates various productivity tools for seamless workflow automation.",
          differentiation:
            response.data.data.differentiation ||
            "Mobile-first design optimized for on-the-go access.\nRobust integration with 50+ widely-used digital tools.\nAI-powered insights and automation specifically tailored for remote teams.",
          howItHelps:
            response.data.data.howItHelps ||
            "Provides a centralized hub for project updates, team communication, and task management, making it significantly easier to stay on track with work responsibilities and deadlines, regardless of location or time zone.",
          features:
            prev.features ||
            "Comprehensive project management dashboard with real-time updates.\nIntegrated chat, video conferencing, and screen sharing capabilities.\nAutomated workflow triggers and smart notifications.\nAdvanced analytics and reporting tools.",
          benefits:
            response.data.data.benefits ||
            "Increases team productivity by 40% on average.\nReduces communication gaps and project delays.\nEnhances accountability with transparent progress tracking.\nScales efficiently with growing teams.",
          tone:
            response.data.data.brandToneAndVoice ||
            "Professional, innovative, user-focused, reliable",
          colors: response.data.data.brandColors || [
            "#3B82F6",
            "#1E40AF",
            "#10B981",
            "#F59E0B",
          ],
        }));
        setIsAnalyzing(false);
      }, 2500);
    } catch (err) {
      setIsAnalyzing(false);
      setApiError("Failed to save brand. Please try again.");
      toastify("error", err.response.data.message);
      console.error(err);
    }
  };

  const handleFileUpload = (file) => {
    setFormData((prev) => ({ ...prev, brandDocument: file }));
    if (file) handleDocumentAnalysis(file);
  };

  const handleDocumentAnalysis = (file) => {
    setIsProcessingFile(true);
    setTimeout(() => {
      setFormData((prev) => ({
        ...prev,
        description:
          prev.description ||
          "AI-powered productivity software for modern teams extracted from document",
        industry: prev.industry || "Technology",
        targetMarket:
          prev.targetMarket ||
          "Small to medium businesses, remote teams, productivity-focused professionals",
        mainJob:
          prev.mainJob ||
          "Facilitates remote project management and team coordination based on document analysis",
        differentiation:
          prev.differentiation ||
          "Mobile-first design with robust integrations extracted from brand document",
        howItHelps:
          prev.howItHelps ||
          "Provides centralized hub for project updates and team communication",
        features:
          prev.features ||
          "Project management dashboard, chat, video conferencing, automated workflows",
        benefits:
          prev.benefits ||
          "Increases team productivity, reduces communication gaps, enhances accountability",
        tone: prev.tone || "Professional, innovative, user-focused",
      }));
      setIsProcessingFile(false);
    }, 2000);
  };

  const handleSave = async (content) => {
    setIsGenerating(true);
    const errors = [];
    if (!formData.name.trim()) {
      errors.push("Brand name is required");
      toastify("error", "Brand name is required");
    }
    if (
      !formData.description.trim() &&
      !formData.website.trim() &&
      !formData.brandDocument
    ) {
      toastify(
        "error",
        "Either description, website URL, or brand document is required"
      );
      errors.push(
        "Either description, website URL, or brand document is required"
      );
    }
    if (errors.length > 0) {
      setIsGenerating(false);
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);
    setApiError("");
    const token = localStorage.getItem("token");
    // Map formData to API payload
    const payload = {
      websiteUrl: UrlConverter(formData?.website),
      brandName: formData.name,
      internalName: formData.internalName,
      isDirectURL: false,
      industry: formData.industry,
      description: formData.description,
      brandColor: formData.colors[0] || "",
      brandColors: formData.colors,
      logo: {
        src: "", // You may want to add logo upload logic
        alt: "main logo",
        width: 0,
        height: 0,
      },
      logos: [], // You may want to add multiple logo upload logic
      mainJobOfProduct: formData.mainJob,
      howItHelps: formData.howItHelps,
      differentiation: formData.differentiation,
      benefits: formData.benefits,
      features: formData.features
        ? formData.features
            ?.split("\n")
            .map((f) => f.trim())
            .filter(Boolean)
        : [],
      targetMarket: formData.targetMarket,
      brandToneAndVoice: formData.tone,
    };

    if (brandId) {
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API}brands/${brandId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toastify("success", response.data.message);
        setIsGenerating(false);
        if (content === "generateContent") {
          localStorage.setItem("selectedBrandId", brandId);
          // setShowGenerateModal(true);
          navigate(
            "/app/ads?brandId=" + brandId + "&brandName=" + formData.name
          );
        } else {
          setTimeout(() => {
            navigate("/app/brands");
          }, 2000);
        }
      } catch (err) {
        setIsGenerating(false);
        setApiError("Failed to save brand. Please try again.");
        toastify("error", err.response.data.message);
        console.error(err);
      }
    } else {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API}brands`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`, // Include the token in the request header
            },
          }
        );
        toastify("success", response.data.message);
        setBrandId(response.data.data._id);
        setIsGenerating(false);
        if (content === "generateContent") {
          localStorage.setItem("selectedBrandId", response.data.data._id);
          navigate(
            "/app/ads?brandId=" +
              response.data.data?._id +
              "&brandName=" +
              formData.name
          );
        } else {
          setTimeout(() => {
            navigate("/app/brands");
          }, 2000);
        }
      } catch (err) {
        setIsGenerating(false);
        setApiError("Failed to save brand. Please try again.");
        toastify("error", err.response.data.message);
        console.error(err);
      }
    }
  };

  const handleCancel = () => {
    navigate("/app/brands");
  };

  const handleGenerateContent = () => {
    // Save the brand first
    handleSave("generateContent");
    // Then open the generate content modal
  };

  return (
    <div className="responsive-page responsive-page--brand-create flex-1 overflow-auto bg-white dark:bg-[#0A0A0A]">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/app/brands")}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-[#fff] flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Brands
            </button>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-gray-600 dark:text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-[#fff]">
                  {formData.name || "Add Brand"}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 truncate lg:w-[580px] overflow-hidden whitespace-nowrap md:w-[400px] sm:w-[200px] w-[150px]">
                  {formData.description ||
                    "Tell us about your brand—what it does, who it’s for, and what makes it stand out."}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCancel}
              className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isGenerating}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              {isGenerating && (
                <CircularLoader size={16} color="border-white" />
              )}
              {isGenerating ? "Saving Brand" : "Save Brand"}
            </button>
          </div>
        </div>

        {apiError && (
          <div className="bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-4 py-2 rounded mb-4">
            {apiError}
          </div>
        )}

        <div className="max-w-6xl mx-auto space-y-6">
          {/* Accelerate Your Brand Setup Section */}
          <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-[#fff] mb-1">
                Accelerate Your Brand Setup
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Save time by letting AI extract your brand information
                automatically
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Smart Website Scanner */}
              <div className="bg-white dark:bg-[#0A0A0A] rounded-lg p-4 border border-orange-100 dark:border-orange-800 shadow-sm flex flex-col">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <span
                      role="img"
                      aria-label="bot"
                      className="text-orange-600 dark:text-orange-400 text-xl"
                    >
                      🤖
                    </span>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900 dark:text-[#fff]">
                      Smart Website Scanner
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      AI-powered brand intelligence
                    </p>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-3 text-xs leading-relaxed">
                  Our AI analyzes your website and learns your visuals, colors,
                  key messaging, and brand story.
                </p>
                <div className="space-y-2 mt-auto">
                  <input
                    value={formData.website}
                    onChange={(e) =>
                      handleInputChange("website", e.target.value)
                    }
                    placeholder="https://yourbrand.com"
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full focus:border-orange-500 focus:ring-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                  />
                  <button
                    type="button"
                    onClick={handleAIAnalysis}
                    disabled={isAnalyzing || !formData.website}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 text-sm rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 flex items-center justify-center"
                  >
                    {isAnalyzing ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Scanning...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">🧠</span>
                        Scan My Website
                      </>
                    )}
                  </button>
                </div>
              </div>
              {/* Brand Document Reader */}
              <div className="bg-white dark:bg-[#0A0A0A] rounded-lg p-4 border border-orange-100 dark:border-orange-800 shadow-sm flex flex-col">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <span
                      role="img"
                      aria-label="upload"
                      className="text-orange-600 dark:text-orange-400 text-xl"
                    >
                      📤
                    </span>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900 dark:text-[#fff]">
                      Brand Document Reader
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Upload & auto-extract
                    </p>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-3 text-xs leading-relaxed">
                  Upload brand docs or guides for instant on-brand AI learning.
                </p>
                <div className="space-y-2 mt-auto">
                  <div className="border-2 border-dashed border-orange-200 dark:border-orange-700 rounded-lg p-3 text-center hover:border-orange-300 dark:hover:border-orange-500 transition-colors bg-orange-25 dark:bg-orange-900/10">
                    <input
                      id="brand-document"
                      type="file"
                      accept=".txt,.pdf,.html,.doc,.docx"
                      onChange={(e) =>
                        handleFileUpload(e.target.files?.[0] || null)
                      }
                      className="hidden"
                    />
                    <label htmlFor="brand-document" className="cursor-pointer">
                      <span
                        role="img"
                        aria-label="file"
                        className="text-orange-400 dark:text-orange-500 text-2xl"
                      >
                        📄
                      </span>
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {formData.brandDocument
                          ? formData.brandDocument.name
                          : "Drop file or click to browse"}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        PDF, DOC, TXT, HTML
                      </p>
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      document.getElementById("brand-document").click()
                    }
                    className="w-full bg-gray-800 dark:bg-gray-700 hover:bg-gray-900 dark:hover:bg-gray-600 text-white font-medium py-2 text-sm rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 flex items-center justify-center"
                    disabled={isProcessingFile}
                  >
                    {isProcessingFile ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">📤</span>
                        Upload Brand Doc
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            {/* Pro Tip */}
            <div className="mt-4 p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-700">
              <div className="flex items-start gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-600 flex items-center justify-center mt-0.5">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <div>
                  <p className="text-xs font-medium text-orange-800 dark:text-orange-300 mb-0.5">
                    Pro Tip: Review Brand Info
                  </p>
                  <p className="text-xs text-orange-700 dark:text-orange-400">
                    Take a quick peek — it should already sound just like you.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="lg:col-span-2 bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                Basic Information
              </h3>
              {validationErrors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <div className="flex items-center">
                    <span className="text-red-600 dark:text-red-400 mr-2">
                      ❌
                    </span>
                    <span className="text-sm font-medium text-red-800 dark:text-red-300">
                      Please fix the following:
                    </span>
                  </div>
                  <ul className="mt-2 text-sm text-red-700 dark:text-red-400 list-disc list-inside">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    What do you call this project?
                  </label>
                  <textarea
                    rows={2}
                    type="text"
                    value={formData.internalName}
                    onChange={(e) =>
                      handleInputChange("internalName", e.target.value)
                    }
                    className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                    placeholder="e.g., Holiday Launch, New Product Drop, or Spring Campaign"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This name helps you identify different versions of the same
                    brand. (e.g. "Acme Holiday Launch" or "Acme Black Friday
                    Sale"). Only visible to you.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      What’s your brand called?
                    </label>
                    <textarea
                      rows={2}
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                      placeholder="e.g., Luna Skincare, GrowthLab, or StudioFlow"
                      required
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      The official brand name used in all generated content.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      What industry are you in?
                    </label>
                    <textarea
                      rows={2}
                      type="text"
                      value={formData.industry}
                      onChange={(e) =>
                        handleInputChange("industry", e.target.value)
                      }
                      className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                      placeholder="e.g., Beauty, Education, Wellness, or Tech"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    What’s your brand about?
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full min-h-[80px] focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                    placeholder="e.g., We help small businesses create effortless marketing with AI so they can focus on what they love. Include your vibe or style notes (like warm colors, real visuals, and a friendly tone)."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Brand website or link
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) =>
                      handleInputChange("website", e.target.value)
                    }
                    className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                    placeholder="e.g., https://yourbrand.com or your landing page URL"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Either a description, website URL, or brand document is
                    required to create a brand
                  </p>
                </div>
              </div>
            </div>

            {/* Brand Colors */}
            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff]">
                  Brand Colors
                </h3>
                <button
                  type="button"
                  onClick={addColor}
                  className="border border-gray-300 dark:border-gray-600 px-2 py-1 rounded text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Add Color
                </button>
              </div>
              <div className="space-y-4">
                {formData.colors.map((color, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {index === 0 && "Primary Color"}
                        {index === 1 && "Secondary Color"}
                        {index > 1 && `Color ${index + 1}`}
                      </label>
                      {formData.colors.length && (
                        <button
                          type="button"
                          onClick={() => removeColor(index)}
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={color}
                        onChange={(e) =>
                          handleColorChange(index, e.target.value)
                        }
                        className="w-12 h-10 rounded border border-gray-300 dark:border-gray-600"
                      />
                      <input
                        value={color}
                        onChange={(e) =>
                          handleColorChange(index, e.target.value)
                        }
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Product Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                What does it actually do?
              </h3>
              <textarea
                value={formData.mainJob}
                onChange={(e) => handleInputChange("mainJob", e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full min-h-[120px] focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                placeholder="e.g., Helps people create content faster, stay consistent, or manage marketing without a team."
              />
            </div>

            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                How does it make life easier?
              </h3>
              <textarea
                value={formData.howItHelps}
                onChange={(e) =>
                  handleInputChange("howItHelps", e.target.value)
                }
                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full min-h-[120px] focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                placeholder="e.g., Simplifies daily marketing, saves time, and gives users confidence to create consistently."
              />
            </div>

            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                What makes it different?
              </h3>
              <textarea
                value={formData.differentiation}
                onChange={(e) =>
                  handleInputChange("differentiation", e.target.value)
                }
                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full min-h-[120px] focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                placeholder="e.g., Unlike others, it learns your brand tone automatically and sends ready-to-post ideas daily."
              />
            </div>

            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                Why people love it
              </h3>
              <textarea
                value={formData.benefits}
                onChange={(e) => handleInputChange("benefits", e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full min-h-[120px] focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                placeholder="e.g., Delivers real results without the overwhelm — people finally feel in control again."
              />
            </div>

            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                What’s included
              </h3>
              <textarea
                value={formData.features}
                onChange={(e) => handleInputChange("features", e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full min-h-[120px] focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                placeholder="e.g., Software tools, online courses, physical products, community access, and coaching services."
              />
            </div>

            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                Who’s it for
              </h3>
              <textarea
                value={formData.targetMarket}
                onChange={(e) =>
                  handleInputChange("targetMarket", e.target.value)
                }
                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full min-h-[120px] focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                placeholder="e.g., Founders, creators, small business owners, or product brands who want simple, consistent ways to grow their brand and connect with their audience."
              />
            </div>
          </div>

          {/* Brand Voice */}
          <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
              How should it sound?
            </h3>
            <textarea
              type="text"
              value={formData.tone}
              onChange={(e) => handleInputChange("tone", e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
              placeholder="e.g., Friendly, confident, and clear. Simple over clever. Helpful, not robotic."
            />
          </div>

          {/* Quick Actions */}
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-2">
                  Ready to create content?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Use this comprehensive brand profile to generate targeted
                  content for your audience.
                </p>
              </div>
              <button
                onClick={handleGenerateContent}
                disabled={isGenerating}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <span className="text-xl">✨</span>
                {isGenerating
                  ? "Save & Generate Content..."
                  : `Save & Generate Content`}
              </button>
            </div>
          </div>

          <CreateVideoModal
            open={showGenerateModal}
            onOpenChange={setShowGenerateModal}
            selectedBrandId={brandId}
            selectedBrandName={formData.name || "New Brand"}
            preselectedContentType={selectedContentFilter}
          />
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default CreateBrand;
